/* eslint-disable @typescript-eslint/no-explicit-any */
/*
 * File: useDonaldGrumpService.ts
 * Project: papa-bear
 * -----
 * Last Modified: 29 Nov 2021 2:08:43 pm
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { encodeValue } from 'common/helpers/encodeValue';
import lodash from 'lodash';
import { ReleasedProductModel } from 'models/ReleasedProductModel';
import { ReleasedProductVariantModel } from 'models/ReleasedProductVariantModel';
import { ReduxState } from 'redux/rootReducer';

/* ****************** Interfaces ****************** */
interface GetReleasedProductParams {
  productNumber: string;
  dataAreaId: string;
}

interface GetReleasedProductsResponseModel {
  data: ReleasedProductModel[];
  totalCount: number;
}

interface GetReleasedProductsParams {
  dataAreaId: string;
  searchName: string | undefined;
  productNumber: string | undefined;
  manufacturerId: string | undefined;
  productGroup: string | undefined;
  offset: number;
}

interface ReleasedProductVariantParams {
  createForProductMasterIfNotExist: boolean;
  dataAreaId: string;
  productNumber: string;
  ocdData: { ownerId: string; conditionId: string; dispositionId: string };
}

/* ****************** Variables ****************** */
const donaldGrumpBaseUrl = process.env.REACT_APP_DONALD_GRUMP_BASE_URL;
const functionsKey = process.env.REACT_APP_API_HOST_KEY_DONALD_GRUMP;

// Check if environment variables are configured
if (!donaldGrumpBaseUrl || !functionsKey) {
  console.error('Missing required environment variables for Donald Grump API:');
  console.error('- REACT_APP_DONALD_GRUMP_BASE_URL:', donaldGrumpBaseUrl);
  console.error('- REACT_APP_API_HOST_KEY_DONALD_GRUMP:', functionsKey);
}

const baseQuery = fetchBaseQuery({
  baseUrl: donaldGrumpBaseUrl,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as ReduxState).app.accessToken;

    if (token) {
      headers.set('authorization', `Bearer ${token}`);
      headers.set('x-functions-key', functionsKey);
      headers.set('Content-Type', 'application/json');
    }

    return headers;
  }
});

/* ****************** Cool Things ****************** */
// const selectProductNumberResult = productsApi.endpoints.getReleasedProduct.select({ productNumber: record.productNumber, dataAreaId: dataAreaId ?? '' });

// const selectProductNumber = createSelector(selectProductNumberResult, (productNumberResult) => productNumberResult.data);
// const apiState = useSelector(selectProductNumber);

/* ****************** RTK Query ****************** */
export const productsApi = createApi({
  reducerPath: 'productsApi',
  baseQuery,
  endpoints: (builder) => ({
    getReleasedProduct: builder.query<ReleasedProductModel, GetReleasedProductParams>({
      query: ({ productNumber, dataAreaId }) => ({
        url: `${dataAreaId}/releasedProducts/${encodeValue({ isUrl: true, value: productNumber })}`,
        responseHandler: (response): Promise<ReleasedProductModel | string> => (response.status >= 300 ? response.text() : response.json())
      }),

      onQueryStarted: async (_arg, _api) => {
        try {
          const { data } = await _api.queryFulfilled;

          sessionStorage.setItem(_arg.productNumber, JSON.stringify(data));
        } catch (error: any) {
          sessionStorage.setItem(_arg.productNumber, JSON.stringify(error.error));
        }
      }
    }),
    getReleasedProducts: builder.query<GetReleasedProductsResponseModel, GetReleasedProductsParams>({
      query: ({ offset, dataAreaId, searchName, productNumber, manufacturerId, productGroup }) => ({
        url: `/releasedProducts`,
        params: {
          offset,
          dataAreaId: dataAreaId ? encodeValue({ isUrl: false, value: dataAreaId }) : undefined,
          searchName: searchName ? encodeValue({ isUrl: false, value: searchName }) : undefined,
          productNumber: productNumber ? encodeValue({ isUrl: false, value: productNumber }) : undefined,
          manufacturerId: manufacturerId ? encodeValue({ isUrl: false, value: manufacturerId }) : undefined,
          productGroup: productGroup ? encodeValue({ isUrl: false, value: productGroup }) : undefined
        }
      }),
      onCacheEntryAdded: async (_arg, _api) => {
        try {
          await _api.cacheDataLoaded;
          const { originalArgs } = _api.getCacheEntry();
          const { queries } = _api.getState().productsApi;
          const lastQueryArgs = { ...originalArgs, offset: _arg.offset - 25 };
          const lastQueryData = Object.values(queries).find((value) => lodash.isEqual(value?.originalArgs, lastQueryArgs));

          if (lastQueryData) {
            _api.updateCachedData((draft) => {
              draft.data.unshift(...(lastQueryData.data as any).data);
            });
          }
        } catch (error) {
          console.log(error);
        }
      }
    }),
    createReleasedProductVariant: builder.mutation<ReleasedProductVariantModel, ReleasedProductVariantParams>({
      query: ({ dataAreaId, productNumber, ocdData, createForProductMasterIfNotExist }) => ({
        url: `${encodeValue({ isUrl: true, value: dataAreaId })}/releasedProducts/${encodeValue({ isUrl: true, value: productNumber })}/releasedProductVariants`,
        params: { createForProductMasterIfNotExist },
        method: 'POST',
        body: ocdData
      }),
      async onQueryStarted(args, { queryFulfilled, dispatch }) {
        try {
          const result = await queryFulfilled;

          dispatch(
            productsApi.util.updateQueryData('getReleasedProduct', { dataAreaId: args.dataAreaId.toUpperCase(), productNumber: args.productNumber }, (product) => {
              product.releasedProductVariants?.push(result.data);
            })
          );
        } catch (err) {
          console.log(err);
        }
      }
    }),
    getVersion: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/version',
        responseHandler: (response): Promise<string> => response.text()
      })
    }),
    getApiName: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/apiName',
        responseHandler: (response): Promise<string> => response.text()
      })
    })
  })
});

export const { useGetReleasedProductQuery, useGetReleasedProductsQuery, useCreateReleasedProductVariantMutation, useLazyGetReleasedProductQuery, useGetVersionQuery, useGetApiNameQuery } = productsApi;
